import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios, usaNuovoToken } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { LINGUE as LINGUE_NATIVE, PRINCIPALE, traduci, useTraduzioni } from '../i18n';
import { leggiSessione } from '../utils/sessione';
import { messaggioErrore, IndicatoreRobustezza, MAX_BYTE_PASSWORD, PASSWORD_LUNGA, passwordTroppoLunga } from '../components/accesso/campi';
import Icona from '../components/cliente/Icone';
import Disponibilita from '../components/Disponibilita';
import ScaricaIMieiDati from '../components/cliente/ScaricaIMieiDati';
import { DispositiviAccessoRapido } from '../components/features/DispositiviAccessoRapido';
import { DispositiviNotifiche } from '../components/features/DispositiviNotifiche';
import CardInstalla from '../components/cliente/CardInstalla';
import { LINGUE } from '../utils/scheda';
import {
  CONTRATTI, DA_AVERE, MAX_COMUNI, PERMESSO_AL, PERMESSO_DAL, PIANI_RICERCA, RELAZIONI, STATI_CIVILI,
  TEMPI_LAVORO, TIPI_IMMOBILE, TIPI_PERMESSO, comuniAuto, nazionalitaIn, pianiDi, scelteIn, senzaComune, tipiDi,
  valorePerServer, controllaCampo, oggiIso, zonaDi,
} from '../utils/profilo';
import MappaZona from '../components/cliente/immobili/MappaZona';
import '../components/staff/staff.css'; // la griglia della disponibilità (sotto .st, come nel calendario)
import '../components/cliente/profilo.css';

// /profilo del cliente (nuovo stile): I miei dati · La mia ricerca · Lavoro e famiglia · Disponibilità · Accesso ·
// Privacy. Ogni campo si salva da solo (all'uscita dal campo o alla scelta), con un "Salvato" discreto.
// Prima di salvare, i controlli di utils/profilo.js (nome non vuoto, data non futura, numeri); se il server rifiuta,
// il campo torna al valore salvato e sotto compare il suo messaggio.

// Il limite del server per i testi del profilo (server.MAX_TESTO_PROFILO): oltre, 400 e il testo scritto spariva
export const MAX_TESTO_PROFILO = 300;

const SCHEDE = ['dati', 'ricerca', 'lavoro', 'disponibilita', 'accesso', 'notifiche', 'privacy'];
// Nome della scheda nella barra; finché la chiave nuova non è nel dizionario vale l'italiano (mai la chiave a video)
const NOMI_SCHEDE_RISERVA = { notifiche: 'Notifiche' };
const nomeScheda = (t, k) => {
  const chiave = `profilo.schede.${k}`;
  const tradotto = t(chiave);
  return tradotto === chiave ? (NOMI_SCHEDE_RISERVA[k] || k) : tradotto;
};

// I nomi delle lingue nel menu «Lingua dei messaggi»: per chi legge in italiano l'elenco di sempre (lo stesso
// della scheda dello staff, utils/scheda.js), per gli altri il nome scritto nella sua lingua (Русский,
// Українська) — che è come si sceglie una lingua, e resta uguale in tutte.
const lingueDelMenu = (lingua) => (lingua === PRINCIPALE ? LINGUE : LINGUE_NATIVE.map((l) => [l.codice, l.nome]));

// Salvataggio di un campo alla volta, con il segno di spunta vicino al campo per due secondi.
// salva() risponde con il messaggio d'errore (null se è andato bene); con avvisa l'errore va anche nel toast.
// Dalla risposta si prende solo il campo salvato (e quelli che il server cambia con lui): con due salvataggi
// sovrapposti (nome, poi subito il cognome) la risposta arrivata per ultima conteneva ancora il valore vecchio
// dell'altro campo e, sostituendo tutto il cliente, lo rimetteva a video.
// citta_ricerca_auto viaggia con i comuni: il server lo ricalcola a ogni risposta, così l'etichetta «aggiunto dagli
// immobili inseriti» sparisce subito dal comune appena tolto (prima restava quella letta all'apertura della pagina).
const COLLEGATI = { piano_ricerca: ['piano_preferito'], tipo_immobile_ricerca: ['tipo_immobile'],
  citta_ricerca: ['citta_ricerca_auto'] };
export const campiDellaRisposta = (campo, risposta) => Object.fromEntries(
  [campo, ...(COLLEGATI[campo] || [])].filter((k) => risposta && k in risposta).map((k) => [k, risposta[k]]),
);
const useSalva = (clienteId, onCliente) => {
  const { t, l: lingua } = useTraduzioni();
  const [salvati, setSalvati] = useState({});
  const timer = useRef({});
  const salva = useCallback(async (campo, valore, { avvisa = true } = {}) => {
    try {
      const r = await axios.put(`${API}/clienti/${clienteId}`, { [campo]: valorePerServer(campo, valore) });
      onCliente(campiDellaRisposta(campo, r.data));
      setSalvati((s) => ({ ...s, [campo]: true }));
      clearTimeout(timer.current[campo]);
      timer.current[campo] = setTimeout(() => setSalvati((s) => ({ ...s, [campo]: false })), 2000);
      return null;
    } catch (e) {
      const messaggio = messaggioErrore(e, t('profilo.non_salvato'), lingua);
      if (avvisa) toast.error(messaggio);
      return messaggio;
    }
  }, [clienteId, onCliente, t, lingua]);
  return { salva, salvati, lingua };
};

const Etichetta = ({ testo, salvato, children, largo, errore, idErrore }) => {
  const { t } = useTraduzioni();
  return (
    <label className={`campo${largo ? ' largo' : ''}`}>
      <span>{testo}{salvato && <em className="salvato" role="status">{t('comune.salvato')}</em>}</span>
      {children}
      {errore && <small className="errore-campo" id={idErrore} role="alert">{errore}</small>}
    </label>
  );
};

// Il valore come lo mostra il campo dopo il salvataggio ("tre" → "3", i comuni senza doppioni)
const comeNelCampo = (campo, valore) => {
  const v = valorePerServer(campo, valore);
  return Array.isArray(v) ? v.join(', ') : String(v ?? '');
};
export const DATA_NON_VALIDA = traduci(PRINCIPALE, 'profilo.data_non_valida');

// Testo che si salva uscendo dal campo (solo se è cambiato). Un valore che non va (controllato qui o rifiutato dal
// server) non resta nel campo: torna quello salvato e sotto si legge perché.
// - I salvataggi di un campo partono uno alla volta, sempre con l'ultimo valore: con la rete lenta quello che si
//   scriveva mentre partiva il salvataggio precedente ("Vasile Ion" → "Vasile Ionel") veniva sovrascritto dalla sua
//   risposta e restava "Vasile Ion". La risposta non tocca più il campo se nel frattempo si è scritto altro.
// - Una data a metà (Backspace sul giorno) o inesistente (31/02) per il browser vale "": prima si salvava vuota, cioè
//   si cancellava la data di nascita, e la pagina diceva "Salvato". Ora non si salva e lo si dice (validity.badInput).
// - Se il server rifiuta quando il campo non è più a video (cambio di scheda), il messaggio arriva in un avviso: prima
//   non si vedeva niente.
const CampoTesto = ({ campo, testo, cliente, s, tipo = 'text', largo, ...resto }) => {
  const { t, l: lingua } = useTraduzioni();
  const iniziale = String(Array.isArray(cliente[campo]) ? cliente[campo].join(', ') : (cliente[campo] ?? ''));
  const [valore, setValore] = useState(iniziale);
  const [errore, setErrore] = useState(null);
  const scritto = useRef(iniziale); // l'ultimo valore scritto nel campo (anche mentre un salvataggio è in volo)
  const confermato = useRef(iniziale); // l'ultimo valore salvato sul server, come lo mostra il campo
  const inVolo = useRef(false);
  const inCoda = useRef(null); // { v }: scritto durante un salvataggio, parte dopo
  const aVideo = useRef(true);
  const input = useRef(null);
  useEffect(() => () => { aVideo.current = false; }, []);
  useEffect(() => {
    confermato.current = iniziale;
    // dati nuovi dal server (pagina riletta, un altro campo salvato): nel campo solo se non si sta scrivendo lì
    if (inVolo.current || inCoda.current || document.activeElement === input.current) return;
    scritto.current = iniziale;
    setValore(iniziale);
  }, [iniziale]);
  const cambia = (v) => { scritto.current = v; setValore(v); if (errore) setErrore(null); };
  const dillo = (problema) => {
    if (aVideo.current) setErrore(problema);
    else toast.error(`${testo}: ${problema.charAt(0).toLowerCase()}${problema.slice(1)}`);
  };
  const salvaInCoda = async (primo) => {
    inVolo.current = true;
    let v = primo;
    let problema = null;
    while (v !== null) {
      if (v !== confermato.current) {
        // eslint-disable-next-line no-await-in-loop
        problema = controllaCampo(campo, v, cliente, undefined, lingua) || await s.salva(campo, v, { avvisa: false });
        if (problema) break;
        confermato.current = comeNelCampo(campo, v);
        // salvato: nel campo il valore come l'ha capito la pagina, se nel frattempo non si è scritto altro
        if (scritto.current === v && aVideo.current) { scritto.current = confermato.current; setValore(confermato.current); }
      }
      v = inCoda.current ? inCoda.current.v : null;
      inCoda.current = null;
    }
    inVolo.current = false;
    inCoda.current = null;
    if (problema) {
      dillo(problema);
      scritto.current = confermato.current;
      if (aVideo.current) setValore(confermato.current);
    }
  };
  const esci = (e) => {
    if (tipo === 'date' && e?.target?.validity?.badInput) {
      dillo(t('profilo.data_non_valida'));
      scritto.current = confermato.current;
      setValore(confermato.current);
      return;
    }
    const v = scritto.current;
    if (inVolo.current) { inCoda.current = { v }; return; }
    if (v === confermato.current) return;
    salvaInCoda(v);
  };
  const idErrore = `errore-${campo}`;
  return (
    <Etichetta testo={testo} salvato={s.salvati[campo]} largo={largo} errore={errore} idErrore={idErrore}>
      <input ref={input} type={tipo} value={valore} onChange={(e) => cambia(e.target.value)} aria-label={testo}
        onBlur={esci} aria-invalid={errore ? true : undefined} aria-describedby={errore ? idErrore : undefined} {...resto} />
    </Etichetta>
  );
};

// Tendina (stato civile, lavoro, lingua): la scelta si vede subito e i salvataggi partono uno alla volta con l'ultima
// scelta (useSalvaInCoda): con due risposte arrivate in ordine inverso a video restava un valore e nel db un altro.
// vuoto = null: niente scelta vuota (la prima opzione vale di base)
const CampoScelta = ({ campo, testo, cliente, s, opzioni, vuoto }) => {
  const { t } = useTraduzioni();
  const nessuno = vuoto === undefined ? t('profilo.scegliete') : vuoto;
  const [scelto, imposta] = useSalvaInCoda(campo, cliente[campo] || '', s);
  return (
    <Etichetta testo={testo} salvato={s.salvati[campo]}>
      <select value={scelto || (nessuno === null ? opzioni[0][0] : '')} onChange={(e) => imposta(e.target.value)} aria-label={testo}>
        {nessuno !== null && <option value="">{nessuno}</option>}
        {opzioni.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
      </select>
    </Etichetta>
  );
};

const Dati = ({ cliente, s }) => {
  const { t, l: lingua } = useTraduzioni();
  return (
    <section className="card r">
      <div className="ttl"><div className="i"><Icona nome="profilo" /></div><h2>{t('profilo.schede.dati')}</h2></div>
      <div className="campi">
        <CampoTesto campo="nome" testo={t('profilo.nome')} cliente={cliente} s={s} autoComplete="given-name" required maxLength={80} />
        <CampoTesto campo="cognome" testo={t('profilo.cognome')} cliente={cliente} s={s} autoComplete="family-name" required maxLength={80} />
        <CampoTesto campo="telefono" testo={t('profilo.telefono')} cliente={cliente} s={s} tipo="tel" autoComplete="tel" maxLength={40} />
        <Etichetta testo={t('profilo.email')}><input value={cliente.email || ''} disabled aria-label={t('profilo.email')} /></Etichetta>
        <CampoTesto campo="data_di_nascita" testo={t('profilo.data_di_nascita')} cliente={cliente} s={s} tipo="date" min="1900-01-01" max={oggiIso()} />
        <CampoTesto campo="codice_fiscale" testo={t('profilo.codice_fiscale')} cliente={cliente} s={s} maxLength={16} style={{ textTransform: 'uppercase' }} />
        <CampoTesto campo="indirizzo_residenza" testo={t('profilo.indirizzo_residenza')} cliente={cliente} s={s} largo autoComplete="street-address" maxLength={200} />
        {/* i nomi delle lingue si scrivono sempre nella loro lingua (Русский, Українська): non si traducono */}
        <CampoScelta campo="lingua" testo={t('profilo.lingua')} cliente={cliente} s={s} opzioni={lingueDelMenu(lingua)} vuoto={null} />
        {/* Il permesso di soggiorno: la data scritta qui comanda su quella letta dal documento, così una lettura
            sbagliata si corregge sempre (backend/permesso_soggiorno.py). Per i familiari sta nel loro modulo. */}
        <CampoScelta campo="permesso_tipo" testo={t('permesso.titolo')} cliente={cliente} s={s}
          opzioni={scelteIn(TIPI_PERMESSO, 'permesso.tipi', lingua)} vuoto={t('permesso.nessuno')} />
        <CampoTesto campo="permesso_scadenza" testo={t('permesso.scadenza')} cliente={cliente} s={s} tipo="date" min={PERMESSO_DAL} max={PERMESSO_AL} />
      </div>
      {cliente.lingua && cliente.lingua !== 'it' && (
        <p className="nota" data-testid="nota-lingua">{t('profilo.nota_lingua')}</p>
      )}
      <p className="nota" data-testid="nota-permesso">
        {t('profilo.nota_permesso')}
        <Link className="link" to="/rinnovo-permesso">{t('permesso.come_si_rinnova_link')}</Link>.
      </p>
      <p className="nota">{t('profilo.nota_email')}</p>
    </section>
  );
};

// Scelte che si salvano al tocco (tipo di casa, piano, da avere): il tocco si vede subito e i salvataggi partono uno
// alla volta, sempre con l'ultimo stato (come la disponibilità). Prima ogni tocco mandava l'elenco calcolato dai dati
// vecchi, con le richieste in parallelo: con la rete lenta restava solo l'ultimo tocco. Se il server rifiuta, si
// torna a quello che è davvero salvato (il messaggio arriva nel toast).
const useSalvaInCoda = (campo, salvato, s) => {
  const [valore, setValore] = useState(salvato);
  const daSalvare = useRef(null); // { v }: l'ultimo stato toccato, che aspetta di partire
  const inVolo = useRef(false);
  const ultimoSalvato = useRef(salvato);
  ultimoSalvato.current = salvato;
  const chiave = JSON.stringify(salvato ?? null);
  // dati nuovi dal server (un altro campo salvato, la pagina riletta): solo se non c'è un tocco in attesa
  useEffect(() => { if (!inVolo.current && !daSalvare.current) setValore(JSON.parse(chiave)); }, [chiave]);
  const svuota = async () => {
    inVolo.current = true;
    let errore = null;
    while (daSalvare.current) {
      const { v } = daSalvare.current;
      daSalvare.current = null;
      // eslint-disable-next-line no-await-in-loop
      errore = await s.salva(campo, v);
    }
    inVolo.current = false;
    if (errore) setValore(ultimoSalvato.current);
  };
  const imposta = (nuovo) => {
    setValore(nuovo);
    daSalvare.current = { v: nuovo };
    if (!inVolo.current) svuota();
  };
  return [valore, imposta];
};

// Più scelte insieme (tipo di casa, piano)
const Scelte = ({ campo, testo, valori, opzioni, s }) => {
  const { t } = useTraduzioni();
  const [scelti, imposta] = useSalvaInCoda(campo, valori, s);
  return (
    <div className="scelte" role="group" aria-label={testo}>
      <span>{testo}{s.salvati[campo] && <em className="salvato">{t('comune.salvato')}</em>}</span>
      {opzioni.map(([v, n]) => {
        const on = scelti.includes(v);
        return <button type="button" key={v} className={on ? 'on' : undefined} aria-pressed={on}
          onClick={() => imposta(on ? scelti.filter((t) => t !== v) : [...scelti, v])}>{n}</button>;
      })}
    </div>
  );
};

// Un sì/no della ricerca (giardino, terrazzo, box): stesso salvataggio in coda
const Interruttore = ({ campo, testo, acceso, s }) => {
  const [on, imposta] = useSalvaInCoda(campo, acceso, s);
  return <button type="button" className={on ? 'on' : undefined} aria-pressed={on} onClick={() => imposta(!on)}>{testo}</button>;
};

// I comuni entrati da soli dagli immobili inseriti (backend/comuni_imparati.py: `citta_ricerca_auto`): portano
// l'etichetta «aggiunto dagli immobili inseriti» e si tolgono con la ✕. La ✕ salva i comuni senza quello, come ogni
// altra modifica del campo: il server si ricorda da sé che è stato rifiutato e non lo rimette più.
// Un comune scritto a mano non arriva mai in `citta_ricerca_auto`: resta com'è, senza etichetta.
const ComuniImparati = ({ cliente, onTogli }) => {
  const { t } = useTraduzioni();
  const [togliendo, setTogliendo] = useState(null);
  const auto = comuniAuto(cliente);
  if (!auto.length) return null;
  const togli = async (comune) => {
    setTogliendo(comune);
    const problema = await onTogli(comune);
    setTogliendo(null);
    if (!problema) toast.success(t('profilo.ricerca.auto_tolto', { comune }));
  };
  return (
    <div className="comuni-auto largo" data-testid="comuni-auto">
      <p className="nota">{t('profilo.ricerca.auto_nota')}</p>
      {auto.map((comune) => (
        <span className="comune-auto" key={comune}>
          <b>{comune}</b>
          <em>{t('profilo.ricerca.auto_etichetta')}</em>
          <button type="button" onClick={() => togli(comune)} disabled={togliendo === comune}
            aria-label={t('profilo.ricerca.auto_togli', { comune })}>✕</button>
        </span>
      ))}
    </div>
  );
};

const Ricerca = ({ cliente, s }) => {
  const { t, l: lingua } = useTraduzioni();
  const salvaComuni = async (campo, valore, opzioni) => {
    if (valorePerServer(campo, valore).length > MAX_COMUNI) return t('profilo.ricerca.max_comuni', { max: MAX_COMUNI });
    return s.salva(campo, valore, opzioni);
  };
  // La zona disegnata sulla mappa: si salvano insieme i poligoni e i comuni che coprono (uno alla volta, così la
  // risposta del secondo non riporta a video il valore vecchio del primo)
  const salvaZona = async (poligoni, comuni) => {
    await s.salva('zona_ricerca', poligoni);
    await s.salva('citta_ricerca', comuni);
  };
  return (
    <section className="card r">
      <div className="ttl"><div className="i"><Icona nome="casa" /></div><h2>{t('profilo.schede.ricerca')}</h2></div>
      <div className="campi">
        <CampoTesto campo="budget_min" testo={t('profilo.ricerca.budget_min')} cliente={cliente} s={s} inputMode="numeric" />
        <CampoTesto campo="budget_max" testo={t('profilo.ricerca.budget_max')} cliente={cliente} s={s} inputMode="numeric" />
        <CampoTesto campo="citta_ricerca" testo={t('profilo.ricerca.comuni', { max: MAX_COMUNI })} cliente={cliente} s={{ ...s, salva: salvaComuni }} largo placeholder={t('profilo.ricerca.comuni_placeholder')} />
        <ComuniImparati cliente={cliente} onTogli={(comune) => salvaComuni('citta_ricerca', senzaComune(cliente.citta_ricerca, comune))} />
        <CampoTesto campo="zone_preferite" testo={t('profilo.ricerca.zone')} cliente={cliente} s={s} largo maxLength={MAX_TESTO_PROFILO} />
        <CampoTesto campo="locali_min" testo={t('profilo.ricerca.locali')} cliente={cliente} s={s} inputMode="numeric" />
        <CampoTesto campo="bagni_min" testo={t('profilo.ricerca.bagni')} cliente={cliente} s={s} inputMode="numeric" />
        <CampoTesto campo="metratura_min" testo={t('profilo.ricerca.metri')} cliente={cliente} s={s} inputMode="numeric" />
      </div>
      <MappaZona poligoni={zonaDi(cliente)} comuni={cliente.citta_ricerca || []} onCambia={salvaZona} idMappa="profilo" />
      <Scelte campo="tipo_immobile_ricerca" testo={t('profilo.ricerca.tipo_casa')} valori={tipiDi(cliente)} opzioni={scelteIn(TIPI_IMMOBILE, 'profilo.scelte.tipi_immobile', lingua)} s={s} />
      <Scelte campo="piano_ricerca" testo={t('profilo.ricerca.piano')} valori={pianiDi(cliente)} opzioni={scelteIn(PIANI_RICERCA, 'profilo.scelte.piani', lingua)} s={s} />
      <div className="scelte" role="group" aria-label={t('profilo.ricerca.da_avere')}>
        <span>{t('profilo.ricerca.da_avere')}{DA_AVERE.some(([k]) => s.salvati[k]) && <em className="salvato">{t('comune.salvato')}</em>}</span>
        {scelteIn(DA_AVERE, 'profilo.scelte.da_avere', lingua).map(([k, n]) => <Interruttore key={k} campo={k} testo={n} acceso={Boolean(cliente[k])} s={s} />)}
      </div>
    </section>
  );
};

const FAMILIARE_VUOTO = { nome: '', cognome: '', relazione: 'coniuge', data_di_nascita: '', nazionalita: '',
  permesso_tipo: '', permesso_scadenza: '' };

// Il familiare come arriva dal server, pronto per il modulo (i campi vuoti sono null, i campi del modulo "")
const perIlModulo = (f) => ({ ...FAMILIARE_VUOTO, ...f, data_di_nascita: f.data_di_nascita || '', nazionalita: f.nazionalita || '',
  permesso_tipo: f.permesso_tipo || '', permesso_scadenza: f.permesso_scadenza || '' });

// Il modulo del familiare: nuovo (Aggiungi) o già salvato (Salva). Mentre si salva il bottone è spento e un secondo
// invio (doppio clic, Invio) non parte; il server rifiuta comunque lo stesso familiare scritto due volte.
const ModuloFamiliare = ({ iniziale, onSalva, onAnnulla }) => {
  const { t, l: lingua } = useTraduzioni();
  const [f, setF] = useState(iniziale);
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState(null);
  const inCorso = useRef(false);
  const campoData = useRef(null);
  const campoScadenza = useRef(null);
  const nuovo = !iniziale.id;
  const invia = async (e) => {
    e.preventDefault();
    if (inCorso.current) return;
    if (!f.nome.trim()) { setErrore(t('profilo.famiglia.scrivete_nome')); return; }
    // data a metà o inesistente: il browser la dà vuota e si sarebbe salvata senza data (come nel profilo, n. 19)
    if (campoData.current?.validity?.badInput) { setErrore(t('profilo.data_non_valida')); return; }
    if (f.data_di_nascita && f.data_di_nascita > oggiIso()) { setErrore(t('profilo.famiglia.nascita_futura')); return; }
    if (campoScadenza.current?.validity?.badInput) { setErrore(t('profilo.data_non_valida')); return; }
    const scadenza = controllaCampo('permesso_scadenza', f.permesso_scadenza, {}, undefined, lingua);
    if (scadenza) { setErrore(scadenza); return; }
    inCorso.current = true;
    setAttesa(true);
    setErrore(null);
    try {
      await onSalva({ nome: f.nome.trim(), cognome: f.cognome.trim(), relazione: f.relazione, data_di_nascita: f.data_di_nascita || null,
        nazionalita: f.nazionalita.trim() || null, permesso_tipo: f.permesso_tipo || null, permesso_scadenza: f.permesso_scadenza || null });
    } catch (err) {
      setErrore(messaggioErrore(err, t(nuovo ? 'profilo.non_aggiunto' : 'profilo.non_salvato'), lingua));
    } finally {
      inCorso.current = false;
      setAttesa(false);
    }
  };
  const campo = (k) => ({ value: f[k] ?? '', onChange: (e) => setF({ ...f, [k]: e.target.value }) });
  return (
    <form className="campi" onSubmit={invia} data-testid="modulo-familiare">
      <label className="campo"><span>{t('profilo.nome')}</span><input {...campo('nome')} required maxLength={80} aria-label={t('profilo.famiglia.nome_aria')} /></label>
      <label className="campo"><span>{t('profilo.cognome')}</span><input {...campo('cognome')} maxLength={80} aria-label={t('profilo.famiglia.cognome_aria')} /></label>
      <label className="campo"><span>{t('profilo.famiglia.chi_e')}</span><select {...campo('relazione')} aria-label={t('profilo.famiglia.relazione')}>
        {scelteIn(RELAZIONI, 'profilo.scelte.relazioni', lingua).map(([v, n]) => <option key={v} value={v}>{n}</option>)}</select></label>
      <label className="campo"><span>{t('profilo.data_di_nascita')}</span><input type="date" ref={campoData} {...campo('data_di_nascita')} min="1900-01-01" max={oggiIso()} aria-label={t('profilo.famiglia.nascita_aria')} /></label>
      <label className="campo"><span>{t('profilo.lavoro.nazionalita')}</span><input {...campo('nazionalita')} list="nazionalita-elenco" maxLength={60} aria-label={t('profilo.famiglia.nazionalita_aria')} /></label>
      {/* il permesso di soggiorno vale per ogni persona del nucleo: anche per lui arrivano i promemoria */}
      <label className="campo"><span>{t('permesso.titolo')}</span><select {...campo('permesso_tipo')} aria-label={t('profilo.famiglia.permesso_aria')}>
        <option value="">{t('profilo.famiglia.non_ne_ha_bisogno')}</option>
        {scelteIn(TIPI_PERMESSO, 'permesso.tipi', lingua).map(([v, n]) => <option key={v} value={v}>{n}</option>)}</select></label>
      <label className="campo"><span>{t('permesso.scadenza')}</span><input type="date" ref={campoScadenza} {...campo('permesso_scadenza')}
        min={PERMESSO_DAL} max={PERMESSO_AL} aria-label={t('profilo.famiglia.scadenza_aria')} /></label>
      {errore && <p className="errore-campo largo" role="alert">{errore}</p>}
      <div className="bottoni">
        <button type="submit" className="btn p" disabled={attesa} aria-busy={attesa}>{t(nuovo ? 'comune.aggiungi' : 'comune.salva')}</button>
        <button type="button" className="btn" onClick={onAnnulla} disabled={attesa}>{t('comune.annulla')}</button>
      </div>
    </form>
  );
};

// Se l'elenco non arriva (errore del server) lo dice, con "Riprovate": prima sembrava che non ci fossero familiari
// (e si poteva aggiungere un doppione)
const Familiari = ({ clienteId }) => {
  const { t, l: lingua } = useTraduzioni();
  const [elenco, setElenco] = useState(null); // null = non ancora arrivato
  const [errore, setErrore] = useState(false);
  const [aperto, setAperto] = useState(null); // il familiare nel modulo: FAMILIARE_VUOTO (nuovo) o uno dell'elenco
  const [daTogliere, setDaTogliere] = useState(null);
  const [togliendo, setTogliendo] = useState(false);
  const carica = useCallback(() => {
    setErrore(false);
    return axios.get(`${API}/clienti/${clienteId}/familiari`)
      .then((r) => setElenco(Array.isArray(r.data) ? r.data : []))
      .catch(() => setErrore(true));
  }, [clienteId]);
  useEffect(() => { carica(); }, [carica]);
  // Familiari cambiati da un'altra scheda (o dallo staff): il server risponde 404 (tolto) o 409 (c'è già). Prima
  // l'elenco restava quello vecchio; ora sui 4xx si rilegge e lo si dice.
  const rifiuto = (err) => {
    const stato = err?.response?.status;
    return stato >= 400 && stato < 500 && stato !== 401 && stato !== 400 ? stato : null;
  };
  const salva = async (dati) => {
    try {
      if (aperto.id) await axios.put(`${API}/clienti/${clienteId}/familiari/${aperto.id}`, dati);
      else await axios.post(`${API}/clienti/${clienteId}/familiari`, dati);
    } catch (err) {
      const stato = rifiuto(err);
      if (!stato) throw err; // il messaggio resta nel modulo (dati da correggere, rete, 5xx)
      carica();
      if (stato === 404) {
        setAperto(null);
        toast.error(t('profilo.famiglia.non_piu', { nome: aperto.nome || dati.nome }));
        return;
      }
      throw Object.assign(new Error('rifiutato'), {
        response: {
          status: stato,
          data: { detail: t('profilo.famiglia.elenco_aggiornato', { messaggio: messaggioErrore(err, t('profilo.famiglia.non_salvato'), lingua).replace(/\.$/, '') }) },
        },
      });
    }
    toast.success(t(aperto.id ? 'comune.salvato' : 'profilo.famiglia.aggiunto'));
    setAperto(null);
    carica();
  };
  const togli = async (f) => {
    setTogliendo(true);
    try { await axios.delete(`${API}/clienti/${clienteId}/familiari/${f.id}`); setDaTogliere(null); carica(); } catch (err) {
      const stato = rifiuto(err);
      if (stato) {
        setDaTogliere(null);
        carica();
        toast(stato === 404 ? t('profilo.famiglia.non_piu', { nome: f.nome })
          : t('profilo.famiglia.elenco_aggiornato', { messaggio: messaggioErrore(err, t('profilo.famiglia.non_tolto'), lingua).replace(/\.$/, '') }));
      } else toast.error(messaggioErrore(err, t('profilo.famiglia.non_tolto_riprovate'), lingua));
    } finally { setTogliendo(false); }
  };
  const nomeRelazione = (v) => (scelteIn(RELAZIONI, 'profilo.scelte.relazioni', lingua).find((r) => r[0] === v) || [0, v])[1];
  if (errore || !elenco) {
    return (
      <div className="familiari" data-testid="familiari">
        <b>{t('profilo.famiglia.titolo')}</b>
        {errore ? (
          <p className="errore-campo" role="alert">
            {t('profilo.famiglia.non_letti')}{' '}
            <a href="#riprova" className="link" onClick={(e) => { e.preventDefault(); carica(); }}><u>{t('comune.riprova')}</u></a>
          </p>
        ) : <p className="nota" role="status">{t('profilo.famiglia.carico')}</p>}
      </div>
    );
  }
  return (
    <div className="familiari" data-testid="familiari">
      <b>{t('profilo.famiglia.titolo')}</b>
      {!elenco.length && !aperto && <p className="nota">{t('profilo.famiglia.vuota')}</p>}
      {elenco.map((f) => (aperto?.id === f.id ? (
        <ModuloFamiliare key={f.id} iniziale={perIlModulo(f)} onSalva={salva} onAnnulla={() => setAperto(null)} />
      ) : (
        <div className="fam" key={f.id} data-testid="familiare">
          <div>
            <b>{f.nome} {f.cognome}</b>
            <span>{[nomeRelazione(f.relazione), f.data_di_nascita && f.data_di_nascita.split('-').reverse().join('/'), f.nazionalita,
              f.permesso_scadenza && t('profilo.famiglia.permesso_fino', { data: f.permesso_scadenza.split('-').reverse().join('/') })].filter(Boolean).join(' · ')}</span>
          </div>
          {daTogliere === f.id ? (
            <div className="conferma" role="group" aria-label={t('profilo.famiglia.togliere_aria', { nome: f.nome })}>
              <span>{t('profilo.famiglia.togliere', { nome: f.nome })}</span>
              <button type="button" className="btn sm no" onClick={() => togli(f)} disabled={togliendo}>{t('profilo.famiglia.si_togli')}</button>
              <button type="button" className="btn sm" onClick={() => setDaTogliere(null)} disabled={togliendo}>{t('comune.no')}</button>
            </div>
          ) : (
            <div className="bottoni-fam">
              <button type="button" className="btn sm" onClick={() => { setDaTogliere(null); setAperto(f); }} disabled={Boolean(aperto)}>{t('comune.modifica')}</button>
              <button type="button" className="btn sm" onClick={() => setDaTogliere(f.id)} disabled={Boolean(aperto)}>{t('profilo.famiglia.togli')}</button>
            </div>
          )}
        </div>
      )))}
      {aperto && !aperto.id && <ModuloFamiliare iniziale={FAMILIARE_VUOTO} onSalva={salva} onAnnulla={() => setAperto(null)} />}
      {!aperto && <button type="button" className="btn sm" onClick={() => { setDaTogliere(null); setAperto(FAMILIARE_VUOTO); }}>{t('profilo.famiglia.aggiungi')}</button>}
    </div>
  );
};

const Lavoro = ({ cliente, s }) => {
  const { t, l: lingua } = useTraduzioni();
  return (
    <section className="card r">
      <div className="ttl"><div className="i w"><Icona nome="documento" /></div><h2>{t('profilo.schede.lavoro')}</h2></div>
      {/* nota introduttiva: spazio sotto prima dei campi (prima 0 px tra il testo e "NAZIONALITÀ") */}
      <p className="nota intro" data-testid="intro-lavoro">{t('profilo.lavoro.intro')}</p>
      <div className="campi">
        <CampoTesto campo="nazionalita" testo={t('profilo.lavoro.nazionalita')} cliente={cliente} s={s} list="nazionalita-elenco" maxLength={MAX_TESTO_PROFILO} />
        <datalist id="nazionalita-elenco">{nazionalitaIn(lingua).map((n) => <option key={n} value={n} />)}</datalist>
        <CampoScelta campo="tipo_contratto" testo={t('profilo.lavoro.lavoro')} cliente={cliente} s={s} opzioni={scelteIn(CONTRATTI, 'profilo.scelte.contratti', lingua)} />
        <CampoScelta campo="tempo_lavoro" testo={t('profilo.lavoro.da_quanto')} cliente={cliente} s={s} opzioni={scelteIn(TEMPI_LAVORO, 'profilo.scelte.tempi_lavoro', lingua)} />
        <CampoScelta campo="stato_civile" testo={t('profilo.lavoro.stato_civile')} cliente={cliente} s={s} opzioni={scelteIn(STATI_CIVILI, 'profilo.scelte.stati_civili', lingua)} />
      </div>
      <Familiari clienteId={cliente.id} />
    </section>
  );
};

// Chi è la sessione di adesso (lo stesso confronto di AuthContext): ruolo e persona
const chiSta = (s) => (s ? `${s.userType}:${s.userInfo?.id || s.userInfo?.email || ''}` : '');
// Il cambio password risponde con un token nuovo: si tiene solo se nel browser c'è ancora la sessione che l'ha
// chiesto. Con la risposta lenta nel frattempo si può essere usciti («Esci»), oppure può essere entrato un altro
// cliente nella stessa scheda: prima bastava che una sessione ci fosse, e il token del primo finiva sui dati del
// secondo (campanella, calendario e toast dell'altro, poi 403 dappertutto).
const stessaSessione = (prima) => {
  const adesso = leggiSessione();
  return Boolean(prima && adesso && adesso.token === prima.token && chiSta(adesso) === chiSta(prima));
};

const Accesso = () => {
  const { t, l: lingua } = useTraduzioni();
  const [pw, setPw] = useState({ attuale: '', nuova: '', conferma: '' });
  const [attesa, setAttesa] = useState(false);
  const inCorso = useRef(false);
  const cambia = async (e) => {
    e.preventDefault();
    if (pw.nuova !== pw.conferma) { toast.error(t('profilo.accesso.non_uguali')); return; }
    if (passwordTroppoLunga(pw.nuova)) { toast.error(PASSWORD_LUNGA); return; } // bcrypt: solo i primi 72 byte
    if (inCorso.current) return; // doppio Invio: un cambio solo
    inCorso.current = true;
    setAttesa(true);
    // chi ha chiesto il cambio: alla risposta il token nuovo si tiene solo se la sessione nel browser è ancora questa
    const prima = leggiSessione();
    try {
      const r = await axios.post(`${API}/auth/password`, { attuale: pw.attuale, nuova: pw.nuova });
      // le altre sessioni non valgono più: questa resta dentro col token nuovo. Se nel frattempo si è usciti («Esci»
      // con la rete lenta) o è entrato qualcun altro, il token nuovo non si tiene: si chiude subito anche sul server
      const nuovo = r.data?.access_token;
      if (!stessaSessione(prima)) {
        // chi ha cambiato la password non è più qui: niente token e niente avviso a chi c'è adesso
        if (nuovo) axios.post(`${API}/auth/esci`, null, { headers: { Authorization: `Bearer ${nuovo}` } }).catch(() => {});
        return;
      }
      usaNuovoToken(nuovo);
      toast.success(t('profilo.accesso.cambiata'));
      setPw({ attuale: '', nuova: '', conferma: '' });
    } catch (err) {
      if (stessaSessione(prima)) toast.error(messaggioErrore(err, t('profilo.accesso.non_cambiata'), lingua));
    } finally { inCorso.current = false; setAttesa(false); }
  };
  return (
    <>
      <section className="card r">
        <div className="ttl"><div className="i"><Icona nome="chiave" /></div><h2>{t('profilo.accesso.password')}</h2></div>
        <form className="campi" onSubmit={cambia}>
          <label className="campo"><span>{t('profilo.accesso.attuale')}</span><input type="password" autoComplete="current-password" value={pw.attuale} onChange={(e) => setPw({ ...pw, attuale: e.target.value })} aria-label={t('profilo.accesso.attuale')} /></label>
          <label className="campo"><span>{t('profilo.accesso.nuova')}</span><input type="password" autoComplete="new-password" maxLength={MAX_BYTE_PASSWORD} value={pw.nuova} onChange={(e) => setPw({ ...pw, nuova: e.target.value })} aria-label={t('profilo.accesso.nuova')} /></label>
          <label className="campo"><span>{t('profilo.accesso.ripetete')}</span><input type="password" autoComplete="new-password" maxLength={MAX_BYTE_PASSWORD} value={pw.conferma} onChange={(e) => setPw({ ...pw, conferma: e.target.value })} aria-label={t('profilo.accesso.ripetete_aria')} /></label>
          {pw.nuova && <div className="largo"><IndicatoreRobustezza password={pw.nuova} /></div>}
          <div className="bottoni"><button type="submit" className="btn p" disabled={attesa || !pw.nuova}>{t('profilo.accesso.cambia')}</button></div>
        </form>
        <p className="nota">{t('profilo.accesso.nota_codice')}</p>
      </section>
      <section className="card r accesso-rapido"><DispositiviAccessoRapido /></section>
    </>
  );
};

const Privacy = ({ clienteId }) => {
  const { t } = useTraduzioni();
  return (
    <section className="card r" id="privacy">
      <div className="ttl"><div className="i b"><Icona nome="chiave" /></div><h2>{t('profilo.privacy.titolo')}</h2></div>
      <ScaricaIMieiDati clienteId={clienteId} />
      <p className="nota">{t('profilo.privacy.nota')}<Link className="link" to="/privacy" state={{ da: '/profilo#privacy' }}>{t('profilo.privacy.informativa')}</Link>. {t('profilo.privacy.nota_fine')}</p>
    </section>
  );
};

// Sul telefono la barra delle schede è più larga dello schermo e scorre di lato: dove c'è altro compaiono una
// sfumatura e una freccia (prima metà delle schede era nascosta senza segni), e la scheda scelta, anche aprendo
// /profilo#privacy, viene portata in vista (lasciando lo spazio della sfumatura).
const MARGINE_SCHEDA = 44;
const BarraSchede = ({ scheda, onScegli }) => {
  const { t } = useTraduzioni();
  const barra = useRef(null);
  const [lati, setLati] = useState({ sx: false, dx: false });
  const misura = useCallback(() => {
    const el = barra.current;
    if (!el) return;
    const sx = el.scrollLeft > 2;
    const dx = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setLati((prima) => (prima.sx === sx && prima.dx === dx ? prima : { sx, dx }));
  }, []);
  useEffect(() => {
    const el = barra.current;
    const attiva = el?.querySelector('[aria-selected="true"]');
    if (attiva && el.scrollWidth > el.clientWidth) {
      const inizio = attiva.offsetLeft - MARGINE_SCHEDA;
      const fine = attiva.offsetLeft + attiva.offsetWidth + MARGINE_SCHEDA - el.clientWidth;
      if (inizio < el.scrollLeft) el.scrollLeft = Math.max(0, inizio);
      else if (fine > el.scrollLeft) el.scrollLeft = fine;
    }
    misura();
  }, [scheda, misura]);
  useEffect(() => {
    window.addEventListener('resize', misura);
    return () => window.removeEventListener('resize', misura);
  }, [misura]);
  const scorri = (verso) => {
    const el = barra.current;
    if (el) el.scrollLeft += verso * Math.round(el.clientWidth * 0.6);
  };
  return (
    <div className={`schede-profilo${lati.sx ? ' sx' : ''}${lati.dx ? ' dx' : ''}`} data-testid="schede-profilo">
      <div className="tabs-profilo" role="tablist" ref={barra} onScroll={misura}>
        {SCHEDE.map((k) => (
          <button type="button" role="tab" key={k} aria-selected={scheda === k} className={scheda === k ? 'on' : undefined}
            onClick={() => onScegli(k)}>{nomeScheda(t, k)}</button>
        ))}
      </div>
      {/* le frecce sono solo per il tocco e il mouse: con la tastiera le schede si raggiungono già tutte */}
      {lati.sx && <button type="button" className="freccia-schede sx" tabIndex={-1} aria-hidden="true" onClick={() => scorri(-1)}>‹</button>}
      {lati.dx && <button type="button" className="freccia-schede dx" tabIndex={-1} aria-hidden="true" onClick={() => scorri(1)}>›</button>}
    </div>
  );
};

const ProfiloCliente = () => {
  const { t } = useTraduzioni();
  const { user, updateUser } = useAuth();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [errore, setErrore] = useState(false);
  const scheda = SCHEDE.includes(hash.replace('#', '')) ? hash.replace('#', '') : 'dati';
  // campi = solo quelli appena salvati (campiDellaRisposta); il nome in alto a destra se è cambiato nome o cognome.
  // La lingua va anche lì: è quella di tutta l'app (AuthContext → LayoutCliente → FornitoreLingua), quindi appena
  // la si cambia qui cambia subito anche il resto, senza ricaricare la pagina.
  const onCliente = useCallback((campi) => {
    setCliente((c) => ({ ...c, ...campi }));
    const suUtente = Object.fromEntries(['nome', 'cognome', 'lingua'].filter((k) => k in campi).map((k) => [k, campi[k]]));
    if (updateUser && Object.keys(suUtente).length) updateUser(suUtente);
  }, [updateUser]);
  const s = useSalva(user?.id, onCliente);
  const carica = useCallback(() => {
    if (!user?.id) return;
    setErrore(false);
    axios.get(`${API}/clienti/${user.id}`).then((r) => setCliente(r.data)).catch(() => setErrore(true));
  }, [user?.id]);
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => {
    const precedente = document.title;
    document.title = `MK Client — ${t('profilo.titolo')}`;
    return () => { document.title = precedente; };
  }, [t]);
  if (!cliente) {
    return (
      <main className="mk-home pagina-profilo wrap">
        <div className="attesa" role="status">
          {errore ? (
            <div className="err">
              {t('profilo.non_letto')}{' '}
              <a href="#riprova" onClick={(e) => { e.preventDefault(); carica(); }}><u>{t('comune.riprova')}</u></a>
            </div>
          ) : <span>{t('profilo.carico')}</span>}
        </div>
      </main>
    );
  }
  return (
    <main className="mk-home pagina-profilo wrap" data-testid="profilo-cliente">
      <div className="testa-doc r">
        <div>
          <div className="k">{t('profilo.occhiello')}</div>
          <h1>{cliente.nome} {cliente.cognome}</h1>
          <p>{t('profilo.sottotitolo')}</p>
        </div>
      </div>
      <BarraSchede scheda={scheda} onScegli={(k) => navigate(`#${k}`, { replace: true })} />
      {scheda === 'dati' && <Dati cliente={cliente} s={s} />}
      {scheda === 'ricerca' && <Ricerca cliente={cliente} s={s} />}
      {scheda === 'lavoro' && <Lavoro cliente={cliente} s={s} />}
      {scheda === 'disponibilita' && <section className="card r"><div className="st"><Disponibilita clienteId={cliente.id} /></div></section>}
      {scheda === 'accesso' && <Accesso />}
      {/* «Installa l'app» sta qui e c'è SEMPRE, anche da computer e ad app già installata: è il posto dove
          uno va a cercarla, e non trovarla sarebbe peggio che vederla e leggere che è già a posto */}
      {scheda === 'notifiche' && <><CardInstalla variante="voce" /><section className="card r accesso-rapido"><DispositiviNotifiche /></section></>}
      {scheda === 'privacy' && <Privacy clienteId={cliente.id} />}
    </main>
  );
};

export default ProfiloCliente;
